/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.platform.config

import com.mobilebytelabs.kmptoolkit.appreview.StoreListing

/**
 * Store identity + prompt policy for the in-app review flow.
 *
 * **SoT: `app-profile/`.** The block below is GENERATED by `./gradlew syncForkConfig` from
 * `identity.app_id`, `platforms/apple/apple.yaml#apple.app_store_id`,
 * `platforms/windows/windows.yaml#windows.store_id`, `org.marketing_url`, and `in_app_review.*`.
 * Change those; do not hand-edit the block.
 *
 * ## Why this exists
 * `AppReviewManagerImpl`'s KDoc names the fork step — *"call `AppReview.configure(StoreListing(...))`
 * once at startup with your store ids"* — and nothing did it. Android, iOS and macOS hid the gap
 * behind their native flows, which need no ids; desktop and web reported `canRequestReview == false`
 * forever, so a fork following the docs would render no affordance and see no error explaining why.
 *
 * The ids are not typed here because every one of them already exists in `app-profile` for the
 * DEPLOY side — the Android applicationId, the App Store numeric id the TestFlight lane uploads
 * against, the Partner Center Store ID. A second hand-typed copy is the drift this projection removes.
 *
 * ## Policy vs. the native prompt
 * [enabled] and the three thresholds gate the AUTOMATIC prompt the app shell raises at launch
 * (`AppViewModel`). They are the app's own restraint, layered on top of the OS's: `promptForReview()`
 * reaches a native flow that Play and StoreKit each rate-limit independently, silently showing
 * nothing when they judge it too soon. Neither layer replaces the other — the OS limit is invisible
 * and unconfigurable, so a fork that wants "not before day 3" has to say so here.
 */
object AppReviewConfig {
    // syncForkConfig:app-review:begin — GENERATED from app-profile. Do not hand-edit.
    /** Play Store package — `identity.app_id`. */
    const val PLAY_STORE_PACKAGE: String = "org.mifos.kmp.template"

    /** App Store numeric id — `platforms/apple/apple.yaml#apple.app_store_id`. */
    const val APP_STORE_ID: String = ""

    /** Microsoft Store product id — `platforms/windows/windows.yaml#windows.store_id`. */
    const val MICROSOFT_STORE_PRODUCT_ID: String = ""

    /** Open-web fallback for targets with no store — `org.marketing_url`. */
    const val WEB_URL: String = "https://mifos.org"

    /** `in_app_review.enabled` — false disables the custom prompt entirely. */
    const val ENABLED: Boolean = true

    /** `in_app_review.min_launches`. */
    const val MIN_LAUNCHES: Int = 5

    /** `in_app_review.min_days_since_install`. */
    const val MIN_DAYS_SINCE_INSTALL: Int = 3

    /** `in_app_review.cooldown_days`. */
    const val COOLDOWN_DAYS: Int = 90
    // syncForkConfig:app-review:end

    /**
     * Whether an AUTOMATIC review prompt is due, given counters the caller keeps.
     *
     * The counters are parameters rather than state owned here, so this stays a pure function of the
     * generated thresholds. `AppReviewPromptStore` keeps them — deliberately outside
     * `UserPreferencesRepository`, because review eligibility is per-DEVICE and must survive
     * sign-out while `clearUserData()` exists to wipe per-user state.
     *
     * This is for a prompt the APP decides to show. A user who taps "Rate this app" has asked
     * already — do not route that through here, call `promptForReview()` directly.
     *
     * @param launchCount completed app launches, including this one.
     * @param daysSinceInstall whole days since first launch.
     * @param daysSinceLastPrompt whole days since the last prompt, or `null` if never prompted.
     */
    fun shouldPromptForReview(
        launchCount: Int,
        daysSinceInstall: Int,
        daysSinceLastPrompt: Int?,
    ): Boolean = ENABLED &&
        launchCount >= MIN_LAUNCHES &&
        daysSinceInstall >= MIN_DAYS_SINCE_INSTALL &&
        (daysSinceLastPrompt == null || daysSinceLastPrompt >= COOLDOWN_DAYS)

    /**
     * The listing to hand `AppReview.configure(...)`.
     *
     * Built from whichever ids are non-blank. A fork that fills none gets an all-blank listing,
     * which `isEmpty` reports as empty and which leaves `canRequestReview` reporting `false` on
     * non-native targets — the same inert state as before, rather than a listing that resolves to
     * a broken store URL. Placeholder values (`YOUR_*`, `example.com`) are treated as blank by the
     * generator for the same reason: a 404 store link is worse than no link.
     */
    val storeListing: StoreListing = StoreListing(
        playStorePackage = PLAY_STORE_PACKAGE,
        appStoreId = APP_STORE_ID,
        microsoftStoreProductId = MICROSOFT_STORE_PRODUCT_ID,
        webUrl = WEB_URL,
    )
}
